<template>
  <div class="file-uploader" :class="{ 'has-files': hasFilesOrProcessing, 'is-expanded': isExpanded }">
    <div v-if="hasFilesOrProcessing" class="files-strip" aria-live="polite" :aria-busy="isProcessing">
      <div v-for="(file, index) in fileContexts" :key="index" class="file-card" :class="file.status">
        <div class="file-icon">
          <img v-if="file.imageUrl" :src="file.imageUrl" :alt="file.filename" class="image-preview" />
          <svg v-else viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path fill="currentColor" d="M6 2h9l5 5v15H6zm8 1.5V8h4.5"/>
            <path fill="#fff" d="M8 12h8v1.5H8zm0 3h8v1.5H8z"/>
          </svg>
        </div>
        <div class="file-info">
          <span class="file-name">{{ file.filename }}</span>
          <span class="file-meta">
            {{ formatFileSize(file.size) }}
            <span class="file-status" :class="file.status">{{ getStatusText(file.status) }}</span>
          </span>
          <span v-if="file.error" class="file-error-tooltip">
            {{ file.error }}
            <span class="tooltip-text">{{ file.error }}</span>
          </span>
        </div>
        <button type="button" class="delete-button" title="移除文件" :aria-label="`移除 ${file.filename}`" :disabled="disabled || isProcessing" @click="removeFile(index)">
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <div v-if="isProcessing && fileContexts.length === 0" class="file-card processing-card">
        <div class="file-icon">
          <div class="spinner"></div>
        </div>
        <div class="file-info">
          <span class="file-name">正在处理文件...</span>
          <span class="file-meta">请稍候</span>
        </div>
      </div>
    </div>

    <slot name="input"></slot>

    <div class="composer-toolbar">
      <button type="button" class="upload-button" :title="`上传图片或文件：Word、Excel、PDF、JPG/PNG/GIF/WebP；最多 ${maxFiles} 个，单文件 ≤10MB，图片合计 ≤6MB，长边 ≤4096 像素`" aria-label="上传图片或文件" aria-describedby="upload-help" @click="triggerFileInput" :disabled="disabled || isProcessing || fileContexts.length >= maxFiles">
        <svg viewBox="0 0 24 24" width="22" height="22">
          <path fill="currentColor" d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z"/>
        </svg>
      </button>
      <input 
        type="file" 
        ref="fileInput" 
        @change="handleFileSelect" 
        multiple 
        style="display: none"
        :accept="`.doc,.docx,.xls,.xlsx,.pdf,${IMAGE_ACCEPT}`"
        aria-label="选择图片或文件"
        :disabled="disabled || isProcessing"
      />
      <slot name="actions"></slot>
    </div>
    <span id="upload-help" class="upload-help">支持 Word、Excel、PDF 和 JPG/PNG/GIF/WebP；最多 {{ maxFiles }} 个文件。文件 ≤10MB，图片合计 ≤6MB，长边 ≤4096 像素。</span>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { IMAGE_ACCEPT, MAX_IMAGE_BYTES, isImageFile, readImage } from '../utils/attachments';
// 静态引入 pdf.js 避免动态导入跨域/阻塞问题
import * as pdfjsLibStatic from 'pdfjs-dist/legacy/build/pdf';

const props = defineProps({
  disabled: { type: Boolean, default: false },
  maxFiles: {
    type: Number,
    default: 10
  },
  maxFileSize: {
    type: Number,
    default: 10 * 1024 * 1024 // 10MB
  }
});

const emit = defineEmits(['update:fileContexts', 'processing']);

const fileInput = ref(null);
const fileContexts = ref([]);
const isProcessing = ref(false);
const isExpanded = ref(false);
const hasFilesOrProcessing = computed(() => fileContexts.value.length > 0 || isProcessing.value);

// 动态加载所需库
let mammoth = null;
let XLSX = null; 
let PDFJS = pdfjsLibStatic; // 使用静态引入的实例
const BASE_URL = process.env.BASE_URL || '/';
const PDF_WORKER_SRC = `${BASE_URL}pdf.worker.min.js`;

const ensurePdfWorker = (pdfjsLib) => {
  if (pdfjsLib?.GlobalWorkerOptions) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
  } else {
    console.warn('PDF.js GlobalWorkerOptions 不可用，无法设置 workerSrc');
  }
};

// 在组件挂载时设置 PDF worker（使用静态引入的 pdf.js）
onMounted(() => {
  try {
    ensurePdfWorker(PDFJS);
  } catch (err) {
    console.error('PDF.js worker setup failed:', err);
  }
});

const loadLibrary = async (libraryName) => {
  switch (libraryName) {
    case 'mammoth':
      if (!mammoth) {
        mammoth = await import('mammoth');
      }
      return mammoth;
    case 'xlsx':
      if (!XLSX) {
        XLSX = await import('xlsx');
      }
      return XLSX;
    case 'pdf':
      // 静态引入的 PDFJS 已可用
      return PDFJS;
    default:
      throw new Error(`Unknown library: ${libraryName}`);
  }
};

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleFileSelect = async (event) => {
  const files = event.target.files;
  if (props.disabled || isProcessing.value || !files || files.length === 0) return;
  
  // 检查是否超过最大文件数
  const remainingSlots = props.maxFiles - fileContexts.value.length;
  const filesToProcess = Array.from(files).slice(0, remainingSlots);
  
  if (filesToProcess.length === 0) return;
  
  isProcessing.value = true;
  emit('processing', true);
  if (typeof window === 'undefined' || window.innerWidth > 640) {
    isExpanded.value = true;
  }
  
  for (const file of filesToProcess) {
    // 检查文件大小
    if (file.size > props.maxFileSize) {
      const fileContext = {
        filename: file.name,
        type: file.type,
        size: file.size,
        content: '',
        status: 'error',
        error: `文件太大 (${(file.size / 1024 / 1024).toFixed(2)}MB)，不能超过${Math.round(props.maxFileSize / 1024 / 1024)}MB`
      };
      fileContexts.value.push(fileContext);
      emit('update:fileContexts', fileContexts.value);
      console.log('文件过大:', file.name);
      continue;
    }
    
    // 验证文件类型
    const fileType = getFileType(file);
    if (fileType === 'unknown') {
      const fileContext = {
        filename: file.name,
        type: file.type,
        size: file.size,
        content: '',
        status: 'error',
        error: '支持 Word、Excel、PDF 和 JPG、PNG、GIF、WebP 图片'
      };
      fileContexts.value.push(fileContext);
      emit('update:fileContexts', fileContexts.value);
      console.log('不支持的文件类型:', file.name);
      continue;
    }
    
    // 添加文件到列表，状态为处理中
    const fileContext = {
      filename: file.name,
      type: file.type,
      size: file.size,
      content: '',
      status: 'processing'
    };
    
    fileContexts.value.push(fileContext);
    emit('update:fileContexts', fileContexts.value);
    
    try {
      await processFile(file, fileContext);
      fileContext.status = 'done';
    } catch (error) {
      console.error('处理文件时出错:', error);
      fileContext.status = 'error';
      fileContext.error = error.message || '处理文件时出错';
    }
    
    emit('update:fileContexts', fileContexts.value);
  }
  
  isProcessing.value = false;
  emit('processing', false);
  
  // 清空文件输入，允许再次选择相同文件
  event.target.value = '';
};

const processFile = async (file, fileContext) => {
  const fileType = getFileType(file);
  
  switch (fileType) {
    case 'image': {
      const totalBytes = fileContexts.value.reduce((sum, item) => sum + (item.imageUrl ? item.size : 0), 0) + file.size;
      if (totalBytes > MAX_IMAGE_BYTES) throw new Error('本次图片合计不能超过 6MB，请减少图片或压缩后重试');
      fileContext.imageUrl = await readImage(file);
      break;
    }
    case 'docx':
      await processDocx(file, fileContext);
      break;
    case 'doc-legacy':
      await processDocLegacy(file, fileContext);
      break;
    case 'xlsx':
    case 'xls':
      await processExcel(file, fileContext);
      break;
    case 'pdf':
      await processPdf(file, fileContext);
      break;
    default:
      throw new Error('不支持的文件类型');
  }
};

const getFileType = (file) => {
  const name = file.name.toLowerCase();
  if (isImageFile(file)) return 'image';
  
  if (name.endsWith('.docx')) return 'docx';
  if (name.endsWith('.doc')) return 'doc-legacy';
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'xlsx';
  if (name.endsWith('.pdf')) return 'pdf';
  
  return 'unknown';
};

const processDocx = async (file, fileContext) => {
  try {
    const { default: mammoth } = await loadLibrary('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    fileContext.content = result.value.trim();
    
    if (!fileContext.content) {
      throw new Error('无法从Word文档中提取文本内容');
    }
  } catch (error) {
    console.error('处理Word文档失败:', error);
    throw new Error('处理Word文档失败: ' + (error.message || '未知错误'));
  }
};

// 简易 .doc 处理：尝试用 mammoth 直接读取；若失败给出明确提示
const processDocLegacy = async (file, fileContext) => {
  try {
    const { default: mammoth } = await loadLibrary('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    // 先尝试用 mammoth（部分 .doc 也能解析）
    try {
      const result = await mammoth.extractRawText({ arrayBuffer });
      fileContext.content = (result.value || '').trim();
    } catch (mammothError) {
      console.warn('mammoth 解析 .doc 失败，尝试兜底提取', mammothError);
    }

    // 如果没有内容，做一次兜底的简单文本提取（过滤不可打印字符）
    if (!fileContext.content) {
      const text = extractPlainTextFromBinary(arrayBuffer);
      fileContext.content = text.trim();
    }
    
    if (!fileContext.content) {
      throw new Error('无法从 .doc 中提取文本内容，请另存为 .docx 或 PDF 后重试');
    }
  } catch (error) {
    console.error('处理 .doc 文件失败:', error);
    throw new Error('解析 .doc 失败，请另存为 .docx 或 PDF 再上传');
  }
};

// 粗粒度的二进制文本提取：提取可打印字符并压缩空白
const extractPlainTextFromBinary = (arrayBuffer) => {
  try {
    // 先尝试 UTF-16LE
    try {
      const utf16 = new TextDecoder('utf-16le', { fatal: false }).decode(arrayBuffer);
      const cleaned = sanitizeText(utf16);
      if (cleaned.length > 50) return cleaned;
    } catch (e) { /* ignore */ }

    // 再尝试 GB18030（中文旧版 doc 常见）
    try {
      const gb = new TextDecoder('gb18030', { fatal: false }).decode(arrayBuffer);
      const cleaned = sanitizeText(gb);
      if (cleaned.length > 50) return cleaned;
    } catch (e) { /* ignore */ }

    // 兜底 Latin1
    const latin = new TextDecoder('windows-1252', { fatal: false }).decode(arrayBuffer);
    return sanitizeText(latin);
  } catch (e) {
    console.error('extractPlainTextFromBinary error', e);
    return '';
  }
};

const sanitizeText = (text) => {
  return text
    .replace(/[\\u0000-\\u0008\\u000B\\u000C\\u000E-\\u001F]/g, ' ')
    .replace(/\\s+/g, ' ')
    .trim();
};

const processExcel = async (file, fileContext) => {
  try {
    const { utils, read } = await import('xlsx');
    const arrayBuffer = await file.arrayBuffer();
    const workbook = read(arrayBuffer, { type: 'array' });
    
    let content = '';
    
    // 处理所有工作表
    workbook.SheetNames.forEach(sheetName => {
      const worksheet = workbook.Sheets[sheetName];
      const csvData = utils.sheet_to_csv(worksheet);
      if (csvData.trim()) {
        content += `工作表: ${sheetName}\n${csvData}\n\n`;
      }
    });
    
    fileContext.content = content.trim();
    
    if (!fileContext.content) {
      throw new Error('无法从Excel文件中提取内容');
    }
  } catch (error) {
    console.error('处理Excel文件失败:', error);
    throw new Error('处理Excel文件失败: ' + (error.message || '未知错误'));
  }
};

const processPdf = async (file, fileContext) => {
  try {
    const pdfjsLib = await loadLibrary('pdf'); // 获取已配置worker的pdfjsLib实例
    // 确保 workerSrc 指向本地静态文件
    ensurePdfWorker(pdfjsLib);
    
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
    const pdf = await loadingTask.promise;
    
    let content = '';
    const maxPages = Math.min(pdf.numPages, 20);
    
    for (let i = 1; i <= maxPages; i++) {
      try {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        content += `页面 ${i}:\n${pageText}\n\n`;
      } catch (pageError) {
        console.error(`提取PDF第${i}页时出错:`, pageError);
        content += `页面 ${i}: [提取失败]\n\n`;
      }
    }
    
    if (pdf.numPages > maxPages) {
      content += `[注：文档共 ${pdf.numPages} 页，已提取前 ${maxPages} 页内容]`;
    }
    
    fileContext.content = content.trim();
    
    if (!fileContext.content && pdf.numPages > 0) { // 确保有页面但无内容时也报错
      throw new Error('无法从PDF文件中提取文本内容，文件可能为空或格式不支持。');
    }
  } catch (error) {
    console.error('处理PDF文件失败 (详细信息):', error);
    let errorMessage = '处理PDF文件失败';
    if (error.message) {
      errorMessage += ': ' + error.message;
    } else if (error.name) {
      errorMessage += ' (' + error.name + ')';
    }
    if (error.message && error.message.toLowerCase().includes('worker')) {
        errorMessage += '。请确认 public/pdf.worker.min.js 可访问，或手动刷新后重试。';
    }
    throw new Error(errorMessage);
  }
};

const removeFile = (index) => {
  fileContexts.value.splice(index, 1);
  emit('update:fileContexts', fileContexts.value);
};

const getStatusText = (status) => {
  switch (status) {
    case 'processing': return '处理中';
    case 'done': return '已完成';
    case 'error': return '处理失败';
    default: return '';
  }
};

const formatFileSize = (size = 0) => {
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

// 导出文件上下文供父组件使用
defineExpose({ fileContexts });
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.image-preview {
  width: 40px;
  height: 40px;
  object-fit: contain;
  border-radius: 4px;
}

.upload-help {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.file-uploader {
  position: relative;
  width: min(100%, 1120px);
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e3e8f0;
  border-radius: 26px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:focus-within {
    border-color: $primary-color;
  }

  @media (max-width: 768px) {
    border-radius: 18px;
  }
}

.files-strip {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2px 2px 6px;
  scroll-snap-type: x proximity;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.45);
    border-radius: 999px;
  }
}

.file-card {
  flex: 0 0 260px;
  min-width: 0;
  min-height: 68px;
  border: 1px solid #e4e9f1;
  border-radius: 12px;
  background: #fbfdff;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  scroll-snap-align: start;
  position: relative;

  &.error {
    border-color: #ffd4d4;
    background: #fffafa;
  }

  @media (max-width: 640px) {
    flex-basis: min(76vw, 260px);
    min-height: 62px;
  }
}

.file-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #eef4ff;
  color: #6c83ad;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.file-info {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.file-name {
  color: #1f2937;
  font-size: 0.9rem;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-meta {
  color: #8a94a6;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.78rem;
  line-height: 1.2;
  white-space: nowrap;
}

.file-status {
  color: #6b7280;

  &.processing {
    color: #2b5fb4;
  }
  
  &.done {
    color: #2f7d32;
  }
  
  &.error {
    color: #c62828;
  }
}

.file-error-tooltip {
  position: relative;
  font-size: 0.8rem;
  color: #c62828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  cursor: help;
  margin-left: $spacing-xs;

  .tooltip-text {
    visibility: hidden;
    width: 250px;
    background-color: #555;
    color: #fff;
    text-align: center;
    border-radius: $border-radius-sm;
    padding: $spacing-sm;
    position: absolute;
    z-index: 1;
    bottom: 125%;
    left: 50%;
    margin-left: -125px;
    opacity: 0;
    transition: opacity 0.3s;
    white-space: normal;
    font-size: 0.75rem;

    &::after {
      content: "";
      position: absolute;
      top: 100%;
      left: 50%;
      margin-left: -5px;
      border-width: 5px;
      border-style: solid;
      border-color: #555 transparent transparent transparent;
    }
  }

  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
}

.delete-button {
  padding: 0;
  border: none;
  background: #ffffff;
  color: #8a94a6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  flex: 0 0 26px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  
  &:hover {
    background-color: #fff5f5;
    color: #c62828;
  }
}

.composer-toolbar {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
}

.upload-button {
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #1f2937;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  transition: background-color 0.18s ease, color 0.18s ease;

  &:hover:not(:disabled) {
    background: #f2f5f9;
    color: $primary-color;
  }
  
  &:disabled {
    opacity: 0.42;
    cursor: not-allowed;
  }
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba($primary-color, 0.3);
  border-top: 2px solid $primary-color;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style> 
